/**
 * WHAT THE SHELF SAYS RIGHT NOW, FOR THE LINES ALREADY IN THE CART — in ONE
 * request.
 *
 * The cap written into a line at add time (`snapshot.maxQty`) is what makes
 * the 41st click refusable in the same frame, with no network. It is also, by
 * construction, a memory: the shopper may have added this yesterday, and 244
 * of dk-store's rows are down to their last unit. So both doors that matter —
 * the drawer where a quantity is chosen, and the checkout where the order is
 * placed — take one live reading before they let the shopper act.
 *
 * ── ONE REQUEST, AND WHY THAT IS THE WHOLE DESIGN ──────────────────────────
 *
 * The obvious shape is "re-fetch each line's product". This release exists in
 * part because that shape, applied to product cards, put 1,099 requests on the
 * wire from one tab and took the per-IP budget from 595 remaining to 26 of 600
 * in 16 seconds. `fetchPublicProductWindow` asks for all of the cart's
 * products by id in a single call, so a nine-line basket costs exactly one
 * request — and it is the SAME call the grid already uses, so the answer comes
 * out of the browser's cache as often as not.
 *
 * ── WHAT A FAILURE MEANS HERE ──────────────────────────────────────────────
 *
 * Nothing. A cart whose refresh failed keeps the ceilings it remembered and
 * the server still refuses what it cannot fill. A failed read must never
 * UNCAP a line (that is an oversell) and must never empty a basket (that is a
 * lost order), so the map is simply absent for the lines it could not answer
 * and `applyStockCaps` leaves those exactly as they were.
 */
import { useEffect, useRef, useState } from 'react';
import { fetchPublicProductWindow } from '../api/storeApi';
import { variantPurchaseCap } from '../utils/stockCap';
import { effectiveVariantPrice } from '../utils/productPrice';

/**
 * The live ceiling for every variant under the cart's products, keyed by
 * variant id. A variant belonging to a product the shop has taken down is
 * absent rather than zero — the order path refuses it on its own, and
 * inventing a zero here would tell the shopper the shop sold out of something
 * it simply no longer lists.
 */
export async function readShelfCaps(salonSlug, items) {
  return (await readShelf(salonSlug, items)).caps;
}

/**
 * The same ONE read, answering everything the cart needs from the shelf: each
 * variant's ceiling, its live LIST price (what `snapshot.price` must hold — see
 * `LIST_PRICE_BASIS` in CartContext), and the product rows themselves, which
 * carry what the campaign display rule matches on (category, variant ids).
 */
export async function readShelf(salonSlug, items) {
  const productIds = [...new Set((items ?? []).map((i) => i?.productId).filter(Boolean))];
  if (!salonSlug || productIds.length === 0) return { caps: new Map(), listPrices: new Map(), products: [] };
  const rows = await fetchPublicProductWindow(salonSlug, productIds);
  const caps = new Map();
  const listPrices = new Map();
  for (const product of rows ?? []) {
    for (const variant of product?.variants ?? []) {
      if (!variant?.id) continue;
      caps.set(variant.id, variantPurchaseCap(product, variant));
      const price = effectiveVariantPrice(variant);
      if (price != null) listPrices.set(variant.id, price);
    }
  }
  return { caps, listPrices, products: rows ?? [] };
}

/**
 * Read the shelf once per (shop, set of lines), while `active`.
 *
 * Keyed on the variant ids rather than the array, so a re-render with an
 * equal-but-new array does not re-request — the same rule the catalogue hook
 * follows, for the same reason.
 *
 * `onCaps` is called with the map so the caller can clamp its cart. It is held
 * in a ref: passing an inline arrow (which every caller does) must not be a
 * reason to fetch again.
 */
export default function useCartStockCaps(salonSlug, items, { active = true, onCaps, onPrices } = {}) {
  const [caps, setCaps] = useState(() => new Map());
  const [products, setProducts] = useState([]);
  const [state, setState] = useState('idle'); // idle | loading | ready | failed
  const onCapsRef = useRef(onCaps);
  onCapsRef.current = onCaps;
  const onPricesRef = useRef(onPrices);
  onPricesRef.current = onPrices;

  const key = (items ?? []).map((i) => `${i?.productId}:${i?.variantId}`).join(',');

  useEffect(() => {
    if (!active || !salonSlug || !key) return undefined;
    let cancelled = false;
    setState('loading');
    readShelf(salonSlug, items)
      .then((reading) => {
        if (cancelled) return;
        setCaps(reading.caps);
        setProducts(reading.products);
        setState('ready');
        onCapsRef.current?.(reading.caps);
        onPricesRef.current?.(reading.listPrices);
      })
      .catch(() => { if (!cancelled) setState('failed'); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salonSlug, key, active]);

  return { caps, state, products };
}
